/**
 * Auth Access Module
 *
 * This module provides a centralized way to access Auth0 tokens and auth state
 * using a getter pattern instead of caching values. This ensures tokens are always
 * fresh and reduces the risk of using expired tokens.
 *
 * The module supports both React and non-React contexts by storing references to
 * Auth0's getAccessTokenSilently function and reactive auth/app-ready state.
 *
 * Reactivity note: isAuthenticated() and isAppReady() are plain functions —
 * not hooks. Do NOT use them for React component rendering. Use useContext(AuthContext)
 * or useAuth0() for reactive values in React components.
 */
import { __awaiter } from "tslib";
/**
 * Timeout duration for waiting on token getter initialization (10 seconds)
 */
const TOKEN_GETTER_TIMEOUT_MS = 10000;
/**
 * Reference to the Auth0 getAccessTokenSilently function
 */
let tokenGetter = null;
/**
 * Reference to a force-refresh variant that calls getAccessTokenSilently with cacheMode: 'off'
 */
let forceRefreshTokenGetter = null;
/**
 * In-flight forced refresh promise. Concurrent callers share this promise so only
 * one refresh-token exchange happens at a time (prevents reuse-detection violations).
 */
let pendingForcedRefresh = null;
/**
 * Promise that resolves when the token getter is ready
 */
let tokenGetterReady;
let resolveTokenGetterReady;
let rejectTokenGetterReady;
/**
 * Initialize or reset the ready promise
 */
function createReadyPromise() {
    tokenGetterReady = new Promise((resolve, reject) => {
        resolveTokenGetterReady = resolve;
        rejectTokenGetterReady = reject;
    });
    // Prevent unhandled rejection warnings when the promise is rejected
    // but no one is waiting (e.g., clearTokenGetters called before any getToken calls)
    tokenGetterReady.catch(() => { });
}
// Initialize the ready promise
createReadyPromise();
/**
 * Set the token getter function.
 * This should be called once Auth0 is initialized with the getAccessTokenSilently function.
 *
 * @param getter - A function that returns a promise resolving to an access token
 *
 * @example
 * ```typescript
 * // In a React component with Auth0
 * const { getAccessTokenSilently } = useAuth0();
 * const tenantName = getTenantName();
 * const audience = getAuth0Audience(tenantName);
 *
 * setTokenGetter(async () => {
 *   return await getAccessTokenSilently({ authorizationParams: { audience } });
 * });
 * ```
 */
export function setTokenGetter(getter) {
    // First one wins - ignore subsequent calls (similar to React hooks pattern)
    if (tokenGetter) {
        return;
    }
    tokenGetter = getter;
    rejectTokenGetterReady = null; // Prevent rejection after resolution
    resolveTokenGetterReady();
}
/**
 * Set the force-refresh token getter.
 * This should call getAccessTokenSilently with cacheMode: 'off' to bypass the SDK cache.
 */
export function setForceRefreshTokenGetter(getter) {
    forceRefreshTokenGetter = getter;
}
/**
 * Get an access token using the configured token getter.
 * If the token getter is not yet initialized, this function will wait until it is.
 *
 * @returns A promise that resolves to the access token
 * @throws Error if the token getter fails to retrieve a token
 *
 * @example
 * ```typescript
 * try {
 *   const token = await getToken();
 *   // Use token for API calls
 * } catch (error) {
 *   console.error('Failed to get token:', error);
 * }
 * ```
 */
export function getToken() {
    return __awaiter(this, void 0, void 0, function* () {
        if (tokenGetter) {
            return tokenGetter();
        }
        // Wait for token getter to be initialized with a timeout
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
                reject(new Error('Token getter not initialized within timeout period'));
            }, TOKEN_GETTER_TIMEOUT_MS);
        });
        yield Promise.race([tokenGetterReady, timeoutPromise]);
        // tokenGetter is guaranteed to be set here because:
        // - tokenGetterReady only resolves when setTokenGetter() is called
        // - if clearTokenGetters() is called, the promise rejects (throws above)
        // - if timeout wins, it also rejects (throws above)
        return tokenGetter();
    });
}
/**
 * Get a fresh access token, bypassing the Auth0 SDK cache.
 * Uses cacheMode: 'off' to force a refresh token exchange.
 *
 * Concurrent calls are deduplicated — if a forced refresh is already in-flight,
 * callers share the existing exchange rather than starting a new one.
 * This prevents refresh-token reuse violations when multiple requests
 * simultaneously detect a stale token.
 *
 * Falls back to regular getToken() if no force-refresh getter is configured.
 */
export function getTokenForced() {
    if (pendingForcedRefresh !== null) {
        return pendingForcedRefresh;
    }
    const refresh = forceRefreshTokenGetter ? forceRefreshTokenGetter() : getToken();
    pendingForcedRefresh = refresh.finally(() => {
        pendingForcedRefresh = null;
    });
    return pendingForcedRefresh;
}
/**
 * Clear the token getter reference.
 * This should be called on logout or when the Auth0 context is no longer valid.
 *
 * @example
 * ```typescript
 * // On logout
 * clearTokenGetters();
 * ```
 */
export function clearTokenGetters() {
    tokenGetter = null;
    forceRefreshTokenGetter = null;
    pendingForcedRefresh = null;
    // Reject any pending getToken() calls that are waiting for initialization
    if (rejectTokenGetterReady) {
        rejectTokenGetterReady(new Error('Token getter cleared during authentication'));
        rejectTokenGetterReady = null;
    }
    // Create a fresh promise so that any getToken() calls after clear
    // will wait for the next setTokenGetter() call (e.g., re-login).
    // This is necessary because a rejected promise stays rejected forever.
    createReadyPromise();
}
let authStateGetter = null;
/**
 * Set the auth state getter.
 * Uses plain overwrite semantics — must be re-called when isAuthenticated changes.
 */
export function setAuthStateGetter(getter) {
    authStateGetter = getter;
}
/**
 * Returns whether the user is currently authenticated.
 * Safe default is false before initialization.
 *
 * Non-reactive — for non-React contexts only (e.g. middleware).
 * Use useAuth0().isAuthenticated in React components.
 */
export function isAuthenticated() {
    return authStateGetter ? authStateGetter() : false;
}
let appReadyGetter = null;
/**
 * Set the app ready getter.
 * Uses plain overwrite semantics — must be re-called when isAppLoading changes.
 */
export function setAppReadyGetter(getter) {
    appReadyGetter = getter;
}
/**
 * Returns whether the app is ready to render content.
 * Equivalent to !isAppLoading from AuthContext.
 * Safe default is false before initialization.
 *
 * Non-reactive — for non-React contexts only.
 * Use useContext(AuthContext).isAppLoading in React components.
 */
export function isAppReady() {
    return appReadyGetter ? appReadyGetter() : false;
}
